/**
 * Color Tokens - Sistema de Diseño UNMSM
 *
 * Paleta de colores completa del sistema de diseño.
 * Incluye colores de marca institucional, semánticos y neutros.
 *
 * @module ColorTokens
 */

/**
 * Paleta de colores del sistema de diseño UNMSM
 * Incluye colores de marca, semánticos y neutros
 */
export const COLORS = {
  /**
   * Rojo institucional de la UNMSM
   * Uso: Botones primarios, enlaces, elementos de marca
   */
  primary: {
    50: '#fdeded',
    100: '#f9d2d2',
    200: '#f3baba',
    300: '#eda1a1',
    400: '#e47c7c',
    /** Color base del rojo institucional */
    500: '#d94c4c',
    /** Hover en botones y enlaces */
    600: '#c53838',
    /** Sidebar y header */
    700: '#a32b2b',
    800: '#892727',
    900: '#732424',
    950: '#3e0f0f',
  },

  /**
   * Dorado académico de la UNMSM
   * Uso: Botones secundarios, CTAs, acentos
   */
  accent: {
    50: '#fefcf3',
    100: '#fcf6d9',
    200: '#f9ebae',
    300: '#f5d97a',
    /** Tono brillante para destacar */
    400: '#efc14b',
    /** Color base del dorado académico - CTAs */
    500: '#eab12b',
    /** Hover en CTAs */
    600: '#d5981d',
    700: '#b1781b',
    800: '#91601d',
    900: '#784f1b',
    950: '#452a0b',
  },

  /**
   * Paleta de grises cálidos
   * Uso: Fondos, bordes, texto, elementos neutrales
   */
  gray: {
    /** Fondo principal de la aplicación */
    50: '#f8f8f8',
    /** Fondos de secciones o tarjetas */
    100: '#f1f1f1',
    /** Bordes de inputs o divisores */
    200: '#e9e9e9',
    /** Bordes en hover */
    300: '#d6d6d6',
    /** Placeholders o disabled */
    400: '#b5b5b5',
    500: '#9e9e9e',
    /** Texto secundario, subtítulos, labels */
    600: '#7e7e7e',
    700: '#646464',
    800: '#4b4b4b',
    /** Texto principal */
    900: '#3C3C3C',
    /** Títulos principales (h1, h2) */
    950: '#222222',
  },

  /**
   * Color semántico de éxito
   * Uso: Mensajes de confirmación, estados completados
   */
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    /** Color base para éxito */
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#14532d',
    950: '#052e16',
  },

  /**
   * Color semántico de error/peligro
   * Uso: Errores, validaciones fallidas, acciones destructivas
   */
  error: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    /** Color base para errores */
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
    950: '#450a0a',
  },

  /**
   * Color semántico de advertencia
   * Uso: Alertas, estados pendientes, información importante
   */
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    /** Color base para alertas */
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
    950: '#451a03',
  },

  /**
   * Color semántico informativo
   * Uso: Tooltips, mensajes informativos, ayudas
   */
  info: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    /** Color base para información */
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
    950: '#172554',
  },

  /**
   * Colores especiales
   */
  special: {
    /** Fondo principal blanco */
    white: '#FFFFFF',
    /** Negro puro */
    black: '#000000',
    /** Fondo de superficie */
    surface: '#FFFFFF',
    /** Fondo de la aplicación */
    background: '#f8f8f8',
  },
} as const

/**
 * Tipo para los colores principales (50-950)
 */
export type ColorScale = typeof COLORS.primary

/**
 * Tipo para los colores especiales
 */
export type SpecialColors = typeof COLORS.special
